import { api } from './api';
import { Product } from './products.service';

export interface CourseSection {
  id: string;
  title: string;
  description?: string;
  order: number;
  isVisible: boolean;
  backgroundColor?: string;
  createdAt: string;
  updatedAt: string;
  products: SectionProduct[];
}

export interface SectionProduct {
  id: string;
  productId: string;
  courseSectionId: string;
  order: number;
  createdAt: string;
  product: Product;
}

export interface CreateSectionDto {
  title: string;
  description?: string;
  order?: number;
  isVisible?: boolean;
  backgroundColor?: string;
}

export interface UpdateSectionDto {
  title?: string;
  description?: string;
  order?: number;
  isVisible?: boolean;
  backgroundColor?: string;
}

export interface AddProductDto {
  productId: string;
  order?: number;
}

export interface ReorderSectionsDto {
  sectionIds: string[];
}

export interface ReorderProductsDto {
  productIds: string[];
}

export interface PublicSectionsResponse {
  sections: CourseSection[];
  orphanProducts: Product[];
}

class CourseSectionsService {
  // ========== Public Endpoints ==========

  /**
   * Listar seções públicas (visíveis com produtos publicados)
   * Não requer autenticação
   */
  async findAllPublic(): Promise<PublicSectionsResponse> {
    const { data } = await api.get('/course-sections/public');
    const result = data?.data || data;
    // Handle both old format (array) and new format ({ sections, orphanProducts })
    if (Array.isArray(result)) {
      return { sections: result, orphanProducts: [] };
    }
    return {
      sections: result?.sections || [],
      orphanProducts: result?.orphanProducts || [],
    };
  }

  // ========== Admin Endpoints ==========

  /**
   * Criar nova seção (Admin)
   */
  async create(dto: CreateSectionDto): Promise<CourseSection> {
    const { data } = await api.post('/course-sections', dto);
    return data?.data || data;
  }

  /**
   * Listar todas as seções (Admin)
   */
  async findAll(): Promise<CourseSection[]> {
    const { data } = await api.get('/course-sections');
    return data?.data || data || [];
  }

  /**
   * Buscar seção por ID (Admin)
   */
  async findOne(id: string): Promise<CourseSection> {
    const { data } = await api.get(`/course-sections/${id}`);
    return data?.data || data;
  }

  /**
   * Atualizar seção (Admin)
   */
  async update(id: string, dto: UpdateSectionDto): Promise<CourseSection> {
    const { data } = await api.put(`/course-sections/${id}`, dto);
    return data?.data || data;
  }

  /**
   * Deletar seção (Admin)
   */
  async remove(id: string): Promise<void> {
    await api.delete(`/course-sections/${id}`);
  }

  /**
   * Reordenar seções (Admin)
   */
  async reorder(dto: ReorderSectionsDto): Promise<CourseSection[]> {
    const { data } = await api.post('/course-sections/reorder', dto);
    return data?.data || data || [];
  }

  /**
   * Adicionar produto a uma seção (Admin)
   */
  async addProduct(
    sectionId: string,
    dto: AddProductDto,
  ): Promise<CourseSection> {
    const { data } = await api.post(
      `/course-sections/${sectionId}/products`,
      dto,
    );
    return data?.data || data;
  }

  /**
   * Remover produto de uma seção (Admin)
   */
  async removeProduct(
    sectionId: string,
    productId: string,
  ): Promise<CourseSection> {
    const { data } = await api.delete(
      `/course-sections/${sectionId}/products/${productId}`,
    );
    return data?.data || data;
  }

  /**
   * Reordenar produtos dentro de uma seção (Admin)
   */
  async reorderProducts(
    sectionId: string,
    dto: ReorderProductsDto,
  ): Promise<CourseSection> {
    const { data } = await api.post(
      `/course-sections/${sectionId}/products/reorder`,
      dto,
    );
    return data?.data || data;
  }
}

export const courseSectionsService = new CourseSectionsService();
